/**
 * Performance Optimization Service
 *
 * Provides automatic performance improvements and optimizations
 */

class PerformanceOptimizer {
  constructor() {
    this.isOptimized = false;
    this.lifecycleVersion = 0;
    this.optimizations = new Set();
    this.observers = new Set();
    this.eventListeners = [];
    this.timeouts = new Set();
    this.idleCallbacks = new Set();
  }

  /**
   * Initialize all performance optimizations
   */
  init() {
    if (this.isOptimized) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    this.lifecycleVersion += 1;
    this.isOptimized = true;

    try {
      this.setupImageLazyLoading();
      this.setupIntersectionObserver();
      this.setupResourceHints();
      this.setupCriticalResourcePreloading();
      this.optimizeScrollPerformance();
      this.optimizeBundleLoading();
      this.setupComponentLevelCodeSplitting();
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  isLifecycleActive(lifecycleVersion) {
    return (
      this.isOptimized && this.lifecycleVersion === lifecycleVersion
    );
  }

  addObserver(observer) {
    this.observers.add(observer);
    return observer;
  }

  addOwnedEventListener(target, type, listener, options) {
    target.addEventListener(type, listener, options);
    this.eventListeners.push({ target, type, listener, options });
  }

  cancelOwnedTimeout(timeoutId) {
    if (timeoutId === null || timeoutId === undefined) return;
    clearTimeout(timeoutId);
    this.timeouts.delete(timeoutId);
  }

  scheduleTimeout(callback, delay) {
    const lifecycleVersion = this.lifecycleVersion;
    const timeoutId = setTimeout(() => {
      this.timeouts.delete(timeoutId);
      if (this.isLifecycleActive(lifecycleVersion)) {
        callback();
      }
    }, delay);

    this.timeouts.add(timeoutId);
    return timeoutId;
  }

  scheduleIdleCallback(callback, fallbackDelay = 2000) {
    const lifecycleVersion = this.lifecycleVersion;

    if (typeof window.requestIdleCallback !== "function") {
      return this.scheduleTimeout(callback, fallbackDelay);
    }

    const idleCallbackId = window.requestIdleCallback((deadline) => {
      this.idleCallbacks.delete(idleCallbackId);
      if (this.isLifecycleActive(lifecycleVersion)) {
        callback(deadline);
      }
    });

    this.idleCallbacks.add(idleCallbackId);
    return idleCallbackId;
  }

  /**
   * Setup lazy loading for images
   */
  setupImageLazyLoading() {
    if (typeof window.IntersectionObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    const imageObserver = this.addObserver(
      new window.IntersectionObserver(
        (entries) => {
          if (!this.isLifecycleActive(lifecycleVersion)) return;

          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.classList.remove("lazy");
                imageObserver.unobserve(img);
              }
            }
          });
        },
        {
          rootMargin: "50px 0px",
          threshold: 0.01,
        },
      ),
    );

    document.querySelectorAll("img[data-src]").forEach((img) => {
      imageObserver.observe(img);
    });

    this.optimizations.add("lazy-loading");
  }

  /**
   * Setup intersection observer for component visibility
   */
  setupIntersectionObserver() {
    if (typeof window.IntersectionObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    const componentObserver = this.addObserver(
      new window.IntersectionObserver(
        (entries) => {
          if (!this.isLifecycleActive(lifecycleVersion)) return;

          entries.forEach((entry) => {
            const component = entry.target;

            if (entry.isIntersecting) {
              component.classList.add("visible");
              component.dispatchEvent(
                new CustomEvent("componentVisible", {
                  detail: { ratio: entry.intersectionRatio },
                }),
              );
            } else {
              component.classList.remove("visible");
              component.dispatchEvent(new CustomEvent("componentHidden"));
            }
          });
        },
        {
          rootMargin: "100px 0px",
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      ),
    );

    document.querySelectorAll(".perf-optimize").forEach((component) => {
      componentObserver.observe(component);
    });

    this.optimizations.add("intersection-observer");
  }

  /**
   * Setup resource hints for better loading performance
   */
  setupResourceHints() {
    const head = document.head;

    // DNS prefetch for external domains
    const externalDomains = ["//fonts.googleapis.com", "//fonts.gstatic.com"];

    externalDomains.forEach((domain) => {
      if (
        !document.querySelector(`link[rel="dns-prefetch"][href="${domain}"]`)
      ) {
        const link = document.createElement("link");
        link.rel = "dns-prefetch";
        link.href = domain;
        head.appendChild(link);
      }
    });

    // Preconnect to critical origins
    const criticalOrigins = ["//fonts.googleapis.com"];

    criticalOrigins.forEach((origin) => {
      if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = origin;
        link.crossOrigin = "anonymous";
        head.appendChild(link);
      }
    });

    this.optimizations.add("resource-hints");
  }

  /**
   * Preload critical resources based on user behavior
   */
  setupCriticalResourcePreloading() {
    const resourcesByRoute = {
      "/": ["/dashboard", "/contacts"],
      "/login": ["/dashboard", "/contacts"],
      "/dashboard": ["/contacts", "/dialer", "/campaigns"],
      "/contacts": ["/campaigns", "/dialer"],
      "/campaigns": ["/contacts", "/dialer"],
    };
    const resourcesToPreload =
      resourcesByRoute[window.location.pathname] || [];
    const lifecycleVersion = this.lifecycleVersion;

    const handleMouseOver = (event) => {
      if (!this.isLifecycleActive(lifecycleVersion)) return;
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href^="/"]');
      if (!link || link.dataset.preloaded) return;

      const href = link.getAttribute("href");
      if (resourcesToPreload.includes(href)) {
        this.preloadRoute(href);
        link.dataset.preloaded = "true";
      }
    };

    this.addOwnedEventListener(document, "mouseover", handleMouseOver);
    this.scheduleIdleCallback(() => {
      resourcesToPreload.forEach((route) => this.preloadRoute(route));
    });

    this.optimizations.add("critical-preloading");
  }

  /**
   * Preload a route's resources
   */
  preloadRoute(route) {
    for (const link of document.head.querySelectorAll('link[rel="prefetch"]')) {
      if (link.getAttribute("href") === route) return;
    }

    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = route;
    document.head.appendChild(link);
  }

  /**
   * Optimize scroll performance with passive listeners and throttling
   */
  optimizeScrollPerformance() {
    const lifecycleVersion = this.lifecycleVersion;

    document
      .querySelectorAll("[data-scroll-optimize]")
      .forEach((element) => {
        let scrollEndTimeout = null;
        const handleScroll = () => {
          if (!this.isLifecycleActive(lifecycleVersion)) return;

          document.body.classList.add("scrolling");
          this.cancelOwnedTimeout(scrollEndTimeout);
          scrollEndTimeout = this.scheduleTimeout(() => {
            document.body.classList.remove("scrolling");
            scrollEndTimeout = null;
          }, 150);
        };

        this.addOwnedEventListener(element, "scroll", handleScroll, {
          passive: true,
        });
      });

    this.optimizations.add("scroll-optimization");
  }

  /**
   * Preload likely routes when the rendered route changes
   */
  optimizeBundleLoading() {
    if (typeof window.MutationObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    let previousRoute = window.location.pathname;
    const routeChangeObserver = this.addObserver(
      new window.MutationObserver(() => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;

        const currentRoute = window.location.pathname;
        if (currentRoute !== previousRoute) {
          this.preloadLikelyRoutes(currentRoute);
          previousRoute = currentRoute;
        }
      }),
    );

    routeChangeObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.optimizations.add("bundle-optimization");
  }

  /**
   * Preload likely next routes based on current route
   */
  preloadLikelyRoutes(currentRoute) {
    const routePatterns = {
      "/dashboard": ["/contacts", "/campaigns"],
      "/contacts": ["/campaigns", "/dialer"],
      "/campaigns": ["/contacts", "/dialer"],
      "/dialer": ["/contacts", "/campaigns"],
    };

    const likelyRoutes = routePatterns[currentRoute] || [];
    likelyRoutes.forEach((route) => {
      this.scheduleIdleCallback(() => this.preloadRoute(route), 0);
    });
  }

  /**
   * Get optimization status
   */
  getOptimizationStatus() {
    return {
      isOptimized: this.isOptimized,
      activeOptimizations: Array.from(this.optimizations),
      memoryUsage: this.getMemoryUsage(),
      performanceScore: this.calculatePerformanceScore(),
    };
  }

  /**
   * Get current memory usage
   */
  getMemoryUsage() {
    if ("memory" in performance) {
      const memory = performance.memory;
      return {
        used: (memory.usedJSHeapSize / 1048576).toFixed(2) + " MB",
        total: (memory.totalJSHeapSize / 1048576).toFixed(2) + " MB",
        limit: (memory.jsHeapSizeLimit / 1048576).toFixed(2) + " MB",
        percentage:
          ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(1) +
          "%",
      };
    }
    return null;
  }

  /**
   * Calculate performance score based on various metrics
   */
  calculatePerformanceScore() {
    let score = 100;

    // Deduct points for high memory usage
    if ("memory" in performance) {
      const memory = performance.memory;
      const usagePercent =
        (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
      if (usagePercent > 80) score -= 20;
      else if (usagePercent > 60) score -= 10;
      else if (usagePercent > 40) score -= 5;
    }

    // Add points for active optimizations
    score += this.optimizations.size * 2;

    // Deduct points for missing optimizations
    const maxOptimizations = 7;
    if (this.optimizations.size < maxOptimizations) {
      score -= (maxOptimizations - this.optimizations.size) * 5;
    }

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Setup component-level code splitting
   */
  setupComponentLevelCodeSplitting() {
    if (typeof window.IntersectionObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    const heavyComponents = new Map([
      ["chart", () => import("../components/charts/LazyCharts")],
    ]);

    document
      .querySelectorAll("[data-lazy-component]")
      .forEach((placeholder) => {
        const componentType = placeholder.dataset.lazyComponent;
        const loader = heavyComponents.get(componentType);
        if (!loader) return;

        const loadObserver = this.addObserver(
          new window.IntersectionObserver(
            (entries) => {
              if (!this.isLifecycleActive(lifecycleVersion)) return;

              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                loadObserver.unobserve(entry.target);
                loader().then((module) => {
                  if (!this.isLifecycleActive(lifecycleVersion)) return;
                  placeholder.dispatchEvent(
                    new CustomEvent("componentLoaded", {
                      detail: { module, componentType },
                    }),
                  );
                });
              });
            },
            { rootMargin: "100px" },
          ),
        );

        loadObserver.observe(placeholder);
      });

    this.optimizations.add("component-splitting");
  }

  /**
   * Cleanup all optimizations
   */
  cleanup() {
    this.isOptimized = false;
    this.lifecycleVersion += 1;

    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();

    this.eventListeners.forEach(
      ({ target, type, listener, options }) =>
        target.removeEventListener(type, listener, options),
    );
    this.eventListeners = [];

    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeouts.clear();

    if (typeof window !== "undefined" && window.cancelIdleCallback) {
      this.idleCallbacks.forEach((callbackId) =>
        window.cancelIdleCallback(callbackId),
      );
    }
    this.idleCallbacks.clear();

    if (typeof document !== "undefined") {
      document.body?.classList.remove("scrolling");
    }

    this.optimizations.clear();
  }
}

// Global performance optimizer instance
const performanceOptimizer = new PerformanceOptimizer();


export default performanceOptimizer;
